import { File, Paths } from 'expo-file-system';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { shareAsync } from 'expo-sharing';
import usePayOverTimeStatement from 'features/payOverTime/hooks/usePayOverTimeStatement';
import { StyleSheet, View } from 'react-native';
import PdfViewer from 'shared/components/PdfRenderer/PdfViewer';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

const PayOverTimeStatementPdfScreen = () => {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { statement, errorModel, loading } = usePayOverTimeStatement(id);

  const onShare = async () => {
    let destination: File | null = null;

    try {
      if (statement?.url) {
        destination = new File(Paths.cache, `${statement.fileName}.pdf`);
        const downloadedFile = await File.downloadFileAsync(
          statement.url,
          destination,
          { idempotent: true },
        );
        await shareAsync(downloadedFile.uri);
      }
    } catch (error) {
      logBreadcrumb({
        level: 'error',
        message: 'PayOverTimeStatementPdfScreen onShare error: ' + error,
      });
    } finally {
      if (destination?.exists) {
        destination.delete();
      }
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: statement?.fileName ?? '',
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              trailingAccessories={
                <IconButton
                  iconKey="SHARE"
                  iconButtonAccessibilityLabel="SHARE"
                  iconOnPress={onShare}
                />
              }
            />
          ),
        }}
      />
      {statement?.url ? <PdfViewer url={statement.url} /> : null}
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
  },
});

export default PayOverTimeStatementPdfScreen;
